import type { ExpressionSpecification } from 'maplibre-gl';
import { type HeatmapCategoryKey, isHeatmapCategoryKey } from '../../data/heatmap-config';

/** Cold→hot: long cool fringe so only true peaks go yellow/red (less one-blob saturation). */
const DEFAULT_HEAT_PAINT: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0,
    'rgba(0,0,0,0)',
    0.08,
    'rgba(30,140,200,0.7)',
    0.22,
    'rgba(40,200,140,0.85)',
    0.4,
    'rgba(220,230,40,0.95)',
    0.58,
    'rgb(255,150,30)',
    0.75,
    'rgb(255,55,25)',
    0.9,
    'rgb(220,20,20)',
    1,
    'rgb(255,200,180)',
];

/** Paint ramps keyed by known category. Unknown API keys fall back to `DEFAULT_HEAT_PAINT`. */
const EVENT_PAINT: Partial<Record<HeatmapCategoryKey, ExpressionSpecification>> = {
    kills: [
        'interpolate',
        ['linear'],
        ['heatmap-density'],
        0,
        'rgba(0,0,0,0)',
        // Short fringe → jump to vivid heat so peaks read as distinct hotspots.
        0.06,
        'rgba(25,160,55,0.75)',
        0.18,
        'rgba(90,210,40,0.9)',
        0.32,
        'rgba(220,230,35,0.95)',
        0.48,
        'rgb(255,170,25)',
        0.65,
        'rgb(255,90,20)',
        0.8,
        'rgb(230,30,20)',
        0.92,
        'rgb(200,10,15)',
        1,
        'rgb(255,185,165)',
    ],
    deaths: [
        'interpolate',
        ['linear'],
        ['heatmap-density'],
        0,
        'rgba(0,0,0,0)',
        0.06,
        'rgba(120,30,140,0.75)',
        0.18,
        'rgba(200,35,140,0.9)',
        0.32,
        'rgba(245,60,120,0.95)',
        0.48,
        'rgb(255,100,70)',
        0.65,
        'rgb(255,55,35)',
        0.8,
        'rgb(230,25,25)',
        0.92,
        'rgb(200,10,20)',
        1,
        'rgb(255,195,185)',
    ],
};

/** Shipped heatmap-color for an event type. Heatmap Lab restores this on the production ramp. */
export function heatmapColorExpression(eventType: string): ExpressionSpecification {
    if (!isHeatmapCategoryKey(eventType)) return DEFAULT_HEAT_PAINT;
    return EVENT_PAINT[eventType] ?? DEFAULT_HEAT_PAINT;
}

export function heatLayerPaint(eventType: string): {
    'heatmap-weight': ExpressionSpecification;
    'heatmap-intensity': ExpressionSpecification;
    'heatmap-color': ExpressionSpecification;
    'heatmap-radius': ExpressionSpecification;
    'heatmap-opacity': ExpressionSpecification;
} {
    return {
        // Pre-normalized per bin (see heatmap-weight.ts). Deriving weight from `share`
        // in an expression is what flattened the map: `share = count / peak_cell` puts
        // ~98% of Night City bins inside one narrow band, so every bin contributed the
        // same amount and the result was a coverage map, not an intensity map.
        'heatmap-weight': ['get', 'w'],
        // Weights now carry the signal, so intensity no longer has to shout. Anything
        // much above ~1.2 re-saturates the city into a single blob.
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.9, 3, 1, 5, 1.15],
        'heatmap-color': heatmapColorExpression(eventType),
        // Roughly world-locked: ~doubling per zoom keeps the kernel covering a constant
        // game-world area, so hotspots hold their shape instead of smearing.
        // Stops stay inside 0–5 — this map's maxZoom is 5, so the old 6/10/12/20 stops
        // were unreachable dead code.
        'heatmap-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 4, 3, 12, 5, 44],
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.85, 3, 0.9, 5, 0.92],
    };
}
